import React, { useState } from "react"
import * as Yup from "yup"
import { Form, Formik } from "formik"
import { useQueryClient } from "react-query"
import { toast } from "react-toastify"
import axios from "axios"
import { useConnectedUserNdfDepensesContext } from "../../_PersonnelNdfDepenses"
import { ICreateConnectedUserNdfDepenseGroupedRequestModel } from "../../../../../mes-activites/ndf/core/_models"
import { IHttpErrorResponseModel, IPersonnelNdfDetailModel } from "@common-models/*"
import MyAlert from "@common-utils/MyAlert"
import {
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_MONTANT_INDEMNITE_PAR_KM,
     CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
     CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
} from "@common-constants/*"
import { createPersonnelNdfDepenseGroupedRequest } from "../../../core/_requests"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import { CONST_API_UTILS_GET_GOOGLE_MAPS_DISTANCE_BETWEEN_TWO_POINTS_IN_KM_ENDPOINT } from "../../../../../../../endpoints"

// Subcomponents
import { NdfGroupedJustificatifsSection } from "./components/NdfGroupedJustificatifsSection"
import { NdfGroupedDetailsSection } from "./components/NdfGroupedDetailsSection"
import { NdfSubmitActions } from "./components/NdfSubmitActions"

const ConnectedUserNdfDepenseCreateGrouped = () => {
     const queryClient = useQueryClient()
     const context = useConnectedUserNdfDepensesContext()

     // Note: We use an index-based record because it's a grouped expense with multiple entries
     const [detailsIndemniteKm, setDetailsIndemniteKm] = useState<
          Record<
               number,
               {
                    distanceEnKmEntreDepartEtDestination: number
                    distanceTotal: number
                    montantTTC: number
               }
          >
     >({})

     const validationSchema = Yup.object().shape({
          fichiers: Yup.array().when("childs", {
               is: (childs: any) => Array.isArray(childs) && childs.every(child => child.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM),
               then: schema => schema.notRequired(),
               otherwise: schema =>
                    schema
                         .of(
                              Yup.mixed()
                                   .required("PJ requise")
                                   .test("fileSize", "La taille de la PJ doit être inférieure ou égale à 10MB", (value: any) => {
                                        if (!value) return true
                                        return value.size <= 10000000
                                   })
                                   .test("fileFormat", "La PJ doit être au format JPEG, PNG ou PDF", (value: any) => {
                                        if (!value) return true
                                        return ["application/pdf", "image/jpeg", "image/png"].includes(value.type)
                                   })
                         )
                         .min(1, "Au moins un fichier est requis"),
          }),
          childs: Yup.array().of(
               Yup.object().shape({
                    date: Yup.string()
                         .label("Date")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: schema => schema.notRequired(),
                              otherwise: schema => schema.required("Champ requis"),
                         }),
                    numeroTicket: Yup.number()
                         .label("Numéro de ticket")
                         .typeError("Format incorrect.")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: schema => schema.nullable().notRequired(),
                              otherwise: schema => schema.required("Champ requis"),
                         }),
                    categorie: Yup.string().label("Catégorie").required("Champ requis"),
                    montantTTC: Yup.number()
                         .label("Montant TTC")
                         .when("categorie", {
                              is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: schema => schema.required(),
                         }),
                    description: Yup.string()
                         .label("Description")
                         .when("categorie", {
                              is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: schema => schema.required("Champ requis"),
                              otherwise: schema => schema.notRequired(),
                         }),
                    repasSeulOuEnGroupe: Yup.string()
                         .label("Seul ou en groupe")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
                              then: schema => schema.required("Champ requis"),
                         }),
                    invites: Yup.array()
                         .label("Invités")
                         .when("repasSeulOuEnGroupe", {
                              is: "REPAS_MIDI_EN_GROUPE",
                              then: schema => schema.required("Champ requis"),
                         }),
                    moisAnneeKm: Yup.string()
                         .label("Mois du trajet")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: schema => schema.required("Veuillez sélectionner le mois et l'année"),
                         }),
                    joursKm: Yup.array()
                         .label("Jours sélectionnés")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: schema =>
                                   schema
                                        .min(1, "Veuillez sélectionner au moins un jour dans l'agenda")
                                        .test("exact-jours", "Vous devez sélectionner exactement le même nombre de jours que de trajets", function (value) {
                                             const nbTrajets = this.parent.nbTrajets || 0
                                             return value && value.length === nbTrajets
                                        }),
                         }),
                    adresseDepart: Yup.string()
                         .label("Adresse de départ")
                         .when("categorie", { is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, then: s => s.required("Champ requis") }),
                    adresseArrive: Yup.string()
                         .label("Adresse d'arrivée")
                         .when("categorie", { is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, then: s => s.required("Champ requis") }),
                    nbTrajets: Yup.number()
                         .label("Nombre de trajets")
                         .when("categorie", { is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, then: s => s.required("Champ requis") }),
                    distanceTotale: Yup.number()
                         .label("Distance totale")
                         .when("categorie", { is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, then: s => s.required() }),
                    isAllerRetour: Yup.boolean().label("Aller-retour"),
               })
          ),
     })

     const initialValues = {
          fichiers: [new File([new Blob()], "")] as File[],
          childs: [
               {
                    date: undefined,
                    numeroTicket: "",
                    categorie: "",
                    montantTTC: "",
                    description: "",
                    repasSeulOuEnGroupe: undefined,
                    invites: undefined,
                    adresseDepart: undefined,
                    adresseArrive: undefined,
                    nbTrajets: undefined,
                    distanceTotale: undefined,
                    isAllerRetour: false,
                    moisAnneeKm: "",
                    joursKm: [],
               },
          ],
     }

     function handleSubmit(values: any, { setStatus, setSubmitting, setFieldError }: any) {
          setStatus(null)
          const validChilds = values.childs.filter((item: any) => !!item.categorie)

          const childs: ICreateConnectedUserNdfDepenseGroupedRequestModel[] = validChilds.map((item: any) => ({
               numeroTicket: item.numeroTicket ? parseInt(item.numeroTicket) : undefined,
               categorie: item.categorie,
               montantTTC: item.montantTTC ? parseFloat(item.montantTTC) : 0,
               date: item.date || undefined,
               description: item.description || undefined,
               repasSeulOuEnGroupe: item.repasSeulOuEnGroupe || undefined,
               invites: item.invites || undefined,
               adresseDepart: item.adresseDepart || undefined,
               adresseArrive: item.adresseArrive || undefined,
               nbTrajets: item.nbTrajets ? String(item.nbTrajets) : undefined,
               distanceTotale: item.distanceTotale ? String(item.distanceTotale) : undefined,
               isAllerRetour: item.isAllerRetour ? "1" : "0",
               moisAnneeKm: item.moisAnneeKm || undefined,
               joursKm: item.joursKm?.map(String) || undefined,
          }))
          if (childs.length === 0) {
               toast.error("Veuillez remplir au moins une ligne de dépense.")
               setSubmitting(false)
               return
          }

          FileAndDriveHelper.processFiles(values.fichiers).then(processedFiles => {
               createPersonnelNdfDepenseGroupedRequest(context.ndfId, childs, processedFiles)
                    .then(r => {
                         queryClient.setQueryData([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId], (details: IPersonnelNdfDetailModel[] | undefined) => {
                              if (details) {
                                   const dataWithNewDepense = [r.data, ...details]
                                   context.computeTotalExpenses(dataWithNewDepense)
                                   return dataWithNewDepense
                              }
                              return []
                         })
                         toast.success("L'ajout a bien été pris en compte.")
                         setSubmitting(false)
                         context.setIsAddDepenseModalShown(false)
                         context.setTypeAddDepense(null)
                         context.setIsCardDepenseSimpleCollapsed(false)
                    })
                    .catch(e => {
                         const error: IHttpErrorResponseModel = e.response?.data
                         if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                              for (const key in error.errors) setFieldError(key, error.errors[key])
                         }
                         setStatus(error?.detail)
                         toast.error(error?.detail, { autoClose: false })
                         setSubmitting(false)
                    })
          })
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
               {helpers => {
                    // Logic mapped directly from the original component
                    async function calculateDistance(index: number, departure?: string, arrival?: string) {
                         if (departure && arrival) {
                              return axios
                                   .post<{ distance: number | null }>(CONST_API_UTILS_GET_GOOGLE_MAPS_DISTANCE_BETWEEN_TWO_POINTS_IN_KM_ENDPOINT, { departure, arrival })
                                   .then(({ data: { distance } }) => {
                                        if (distance) {
                                             setDetailsIndemniteKm(prev => ({
                                                  ...prev,
                                                  [index]: { ...prev[index], distanceEnKmEntreDepartEtDestination: distance },
                                             }))
                                        }
                                        return distance || 0
                                   })
                         }
                         return 0
                    }

                    function syncKM(index: number, dist: number, ar: boolean, nb: number) {
                         const dt = dist > 0 ? Number((dist * (ar ? nb * 2 : nb)).toFixed(2)) : 0
                         const montant = Number((dt * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2))

                         setDetailsIndemniteKm(prev => ({
                              ...prev,
                              [index]: { ...prev[index], distanceTotal: dt, montantTTC: montant },
                         }))

                         helpers.setFieldValue(`childs[${index}].distanceTotale`, dt)
                         helpers.setFieldValue(`childs[${index}].montantTTC`, montant)
                    }

                    return (
                         <Form noValidate autoComplete="off">
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-4"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              {/* Layout mirroring the Simple form style */}
                              <div className="row g-9">
                                   <div className="col-lg-7 col-xl-6 order-2 order-lg-1">
                                        <div className="d-flex flex-column gap-4">
                                             <NdfGroupedDetailsSection
                                                  helpers={helpers}
                                                  validationSchema={validationSchema}
                                                  detailsIndemniteKm={detailsIndemniteKm}
                                                  setDetailsIndemniteKm={setDetailsIndemniteKm}
                                                  calculateDistance={calculateDistance}
                                                  syncKM={syncKM}
                                             />

                                             <div className="separator separator-dashed my-6" />
                                             <NdfSubmitActions helpers={helpers} setSubmitAction={() => {}} />
                                        </div>
                                   </div>

                                   <div className="col-lg-5 col-xl-4 order-1 order-lg-2 mb-8 mb-lg-0 animate__animated">
                                        <div className="position-sticky" style={{ top: "25px", zIndex: 1 }}>
                                             <NdfGroupedJustificatifsSection helpers={helpers} />
                                        </div>
                                   </div>
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default ConnectedUserNdfDepenseCreateGrouped
